import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import { imageAPI, imageUtils } from '../../services/imageApi';
import { isManagedMediaUrl } from '../../utils/mediaImageUtils';
import { 
  ArrowLeft, 
  Bed, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Building2,
  Users,
  DollarSign,
  Settings,
  Save,
  X,
  Upload,
  Camera,
  ImageIcon
} from 'lucide-react';
import { roomTaxesFromDiscounted } from '../../utils/roomPricing';
import { normalizeRoomPriceForSave, minDiscountedAcrossWeek } from '../../utils/roomDayPricing';
import RoomWeekdayPricingFields from '../../components/admin/RoomWeekdayPricingFields';
import RoomGuestPricingFields from '../../components/admin/RoomGuestPricingFields';
import {
  ADMIN_ICON_STROKE,
  AdminPageHeader,
  AdminStatGrid,
  AdminSectionTitle,
  AdminTableIconButton,
  AdminTableIconButtonDanger,
  AdminSearchToolbar,
  AdminPaginationBar,
  paginateArray,
  DEFAULT_ADMIN_PAGE_SIZE
} from '../../components/admin/AdminShared';
import DraggableStayImagesGrid from '../../components/DraggableStayImagesGrid';
import { DEFAULT_ROOM_FORM_OPTIONS, mergeRoomFormOptions } from '../../utils/roomBookingOptions';

const AdminRooms = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action');

  const closeRoomForm = () => {
    setShowForm(false);
    setEditingRoom(null);
    if (searchParams.get('action')) {
      navigate('/admin/rooms', { replace: true });
    }
  };

  const [stays, setStays] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [showForm, setShowForm] = useState(action === 'create' || action === 'edit');
  const [editingRoom, setEditingRoom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [formData, setFormData] = useState({
    stayId: '',
    roomId: '',
    title: '',
    description: '',
    capacity: 2,
    beds: 1,
    features: [],
    options: { ...DEFAULT_ROOM_FORM_OPTIONS },
    price: {
      mrp: 0,
      discountedPrice: 0,
      taxes: 0,
      currency: 'INR',
      useDayOfWeekPricing: false,
      dayPricing: {},
      guestPricing: { enabled: false, tiers: [], thresholdAdults: 2, extraAdultAddon: { mrp: 0, discountedPrice: 0 } }
    },
    images: [],
    amenities: []
  });

  const [newFeature, setNewFeature] = useState({ title: '', image: '' });
  const [newAmenity, setNewAmenity] = useState('');
  const [featureImageFile, setFeatureImageFile] = useState(null);
  const [uploadingFeatureImage, setUploadingFeatureImage] = useState(false);
  const [featureImagePreview, setFeatureImagePreview] = useState(null);
  const [useImageUpload, setUseImageUpload] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [listPage, setListPage] = useState(1);

  // Fetch data from API (re-run when navigating here so the stay list is fresh after adding a stay elsewhere)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch stays and rooms in parallel
        const [staysResponse, roomsResponse] = await Promise.all([
          adminApi.getStays(),
          adminApi.getRooms()
        ]);
        
        setStays(staysResponse.stays || []);
        setRooms(roomsResponse.rooms || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback to empty arrays on error
        setStays([]);
        setRooms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location.pathname, location.search]);

  useEffect(() => {
    const act = searchParams.get('action');
    if (act === 'create') {
      setShowForm(true);
      setEditingRoom(null);
    }
  }, [searchParams]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      (async () => {
        try {
          const staysResponse = await adminApi.getStays();
          setStays(staysResponse.stays || []);
        } catch (error) {
          console.error('Error refreshing stays:', error);
        }
      })();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    // Validate files
    const validFiles = [];
    const errors = [];
    
    files.forEach(file => {
      if (!imageUtils.isValidImageType(file)) {
        errors.push(`${file.name}: Invalid file type. Only images are allowed.`);
        return;
      }
      
      if (!imageUtils.isValidFileSize(file)) {
        errors.push(`${file.name}: File too large. Maximum size is 10MB.`);
        return;
      }
      
      validFiles.push(file);
    });
    
    if (errors.length > 0) {
      alert('Upload errors:\n' + errors.join('\n'));
      return;
    }
    
    if (validFiles.length === 0) return;
    
    try {
      setUploadingImages(true);
      
      // Upload to S3
      const response = await imageAPI.uploadImages(validFiles, 'rooms');
      
      if (response.uploaded && response.uploaded.length > 0) {
        const uploadedUrls = response.uploaded.map(item => item.url);
        
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...uploadedUrls]
        }));
        
        if (response.errors && response.errors.length > 0) {
          console.warn('Some images failed to upload:', response.errors);
        }
      } else {
        throw new Error('No images were uploaded successfully');
      }
      
    } catch (error) {
      console.error('Image upload error:', error);
      const details =
        error.response?.data?.details || error.response?.data?.error || error.message;
      alert(details ? `Failed to upload images: ${details}` : 'Failed to upload images. Please try again.');
    } finally {
      setUploadingImages(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const removeImage = async (index) => {
    const imageUrl = formData.images[index];
    
    if (imageUrl && isManagedMediaUrl(imageUrl)) {
      try {
        await imageAPI.deleteImage(imageUrl);
      } catch (error) {
        console.error('Failed to delete image from storage:', error);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const reorderRoomImages = (nextUrls) => {
    setFormData((prev) => ({ ...prev, images: nextUrls }));
  };

  const addFeature = () => {
    if (newFeature.title && newFeature.image) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, { ...newFeature }]
      }));
      setNewFeature({ title: '', image: '' });
      setFeatureImageFile(null);
      setFeatureImagePreview(null);
      setUseImageUpload(false);
    }
  };

  const handleFeatureImageUpload = async (file) => {
    if (!file) return;

    // Validate file
    if (!imageUtils.isValidImageType(file)) {
      alert('Please select a valid image file (JPEG, PNG, WebP, GIF)');
      return;
    }

    if (!imageUtils.isValidFileSize(file)) {
      alert('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingFeatureImage(true);
      
      // Create preview
      const previewUrl = imageUtils.createPreviewUrl(file);
      setFeatureImagePreview(previewUrl);

      // Upload to S3
      const response = await imageAPI.uploadImages([file], 'room-features');
      
      if (response.uploaded && response.uploaded.length > 0) {
        const uploadedImage = response.uploaded[0];
        setNewFeature(prev => ({ ...prev, image: uploadedImage.url }));
        
        // Clean up preview
        imageUtils.revokePreviewUrl(previewUrl);
        setFeatureImagePreview(null);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Feature image upload error:', error);
      const details =
        error.response?.data?.details || error.response?.data?.error || error.message;
      alert(details ? `Failed to upload image: ${details}` : 'Failed to upload image. Please try again.');

      // Clean up preview on error
      if (featureImagePreview) {
        imageUtils.revokePreviewUrl(featureImagePreview);
        setFeatureImagePreview(null);
      }
    } finally {
      setUploadingFeatureImage(false);
    }
  };

  const handleFeatureImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFeatureImageFile(file);
      handleFeatureImageUpload(file);
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addAmenity = () => {
    if (newAmenity.trim()) {
      setFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, newAmenity.trim()]
      }));
      setNewAmenity('');
    }
  };

  const removeAmenity = (index) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        price: normalizeRoomPriceForSave(formData.price, formData.capacity)
      };
      let response;
      if (editingRoom) {
        // Update existing room
        response = await adminApi.updateRoom(formData.stayId, formData.roomId, payload);
      } else {
        // Create new room
        response = await adminApi.createRoom(payload);
      }
      
      console.log('Room saved successfully:', response);
      
      // Reset form and close
      setFormData({
        stayId: '',
        roomId: '',
        title: '',
        description: '',
        capacity: 2,
        beds: 1,
        features: [],
        options: { ...DEFAULT_ROOM_FORM_OPTIONS },
        price: {
          mrp: 0,
          discountedPrice: 0,
          taxes: 0,
          currency: 'INR',
          useDayOfWeekPricing: false,
          dayPricing: {}
        },
        images: [],
        amenities: []
      });
      setEditingRoom(null);
      setShowForm(false);
      
      // Refresh the data
      const [staysResponse, roomsResponse] = await Promise.all([
        adminApi.getStays(),
        adminApi.getRooms()
      ]);
      
      setStays(staysResponse.stays || []);
      setRooms(roomsResponse.rooms || []);

      if (searchParams.get('action')) {
        navigate('/admin/rooms', { replace: true });
      }

    } catch (error) {
      console.error('Error saving room:', error);
      alert('Error saving room. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    const dp = room?.price?.discountedPrice ?? 0;
    setFormData({
      ...room,
      options: mergeRoomFormOptions(room.options),
      price: normalizeRoomPriceForSave(
        {
          ...room.price,
          useDayOfWeekPricing: Boolean(room.price?.useDayOfWeekPricing),
          dayPricing: room.price?.dayPricing && typeof room.price.dayPricing === 'object'
            ? room.price.dayPricing
            : {},
          taxes: roomTaxesFromDiscounted(dp)
        },
        room.capacity
      )
    });
    setShowForm(true);
  };

  const handleDelete = async (stayId, roomId) => {
    if (window.confirm('Are you sure you want to delete this room? This action cannot be undone.')) {
      try {
        setLoading(true);
        await adminApi.deleteRoom(stayId, roomId);
        
        // Refresh the data
        const [staysResponse, roomsResponse] = await Promise.all([
          adminApi.getStays(),
          adminApi.getRooms()
        ]);
        
        setStays(staysResponse.stays || []);
        setRooms(roomsResponse.rooms || []);
        
        alert('Room deleted successfully!');
      } catch (error) {
        console.error('Error deleting room:', error);
        alert('Error deleting room. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const filteredRooms = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    if (!q) return rooms;
    return rooms.filter((room) => {
      const stayTitle =
        room.stayInfo?.title || stays.find((s) => s.stayId === room.stayId)?.title || '';
      const hay = [room.title, room.roomId, room.stayId, room.description, stayTitle]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rooms, stays, listSearch]);

  useEffect(() => {
    setListPage(1);
  }, [listSearch]);

  const { pageItems: pagedRooms, total: filteredTotal, page: safeListPage } = useMemo(
    () => paginateArray(filteredRooms, listPage, DEFAULT_ADMIN_PAGE_SIZE),
    [filteredRooms, listPage]
  );

  if (showForm) {
    return (
      <div>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={closeRoomForm}
              className="p-2.5 rounded-[12px] bg-[#FAF7F2] text-[#FF5E4C] hover:opacity-90 shrink-0 transition-opacity inline-flex items-center justify-center relative z-10"
              aria-label="Back to rooms list"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={ADMIN_ICON_STROKE} aria-hidden />
            </button>
            <div>
              <h1 className="text-[20px] md:text-[22px] font-medium text-black tracking-[-0.02em]">
                {editingRoom ? 'Edit room' : 'Add room'}
              </h1>
              <p className="text-[14px] text-[#78736A] mt-1">Room details and pricing</p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeRoomForm}
            className="px-5 py-2.5 border border-[#E5E3DF] bg-white text-black rounded-full hover:bg-[#FAF7F2] text-[14px] font-medium transition-colors"
          >
            Cancel
          </button>
        </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Basic information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Stay *
                  </label>
                  <select
                    required
                    value={formData.stayId}
                    onChange={(e) => {
                      const newStayId = e.target.value;
                      const matched = stays.find((s) => s.stayId === newStayId);
                      const fromStay =
                        matched && Array.isArray(matched.images) && matched.images.length > 0
                          ? [...matched.images]
                          : [];
                      setFormData((prev) => ({
                        ...prev,
                        stayId: newStayId,
                        images: editingRoom ? prev.images : fromStay
                      }));
                    }}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  >
                    <option value="">Select a stay</option>
                    {stays.map(stay => (
                      <option key={stay.stayId} value={stay.stayId}>
                        {stay.title} - {stay.city}, {stay.state}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Room ID *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.roomId}
                    onChange={(e) => setFormData(prev => ({ ...prev, roomId: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="e.g., r_001"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Room Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="e.g., Deluxe Pet Suite"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Capacity *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    max="20"
                    value={formData.capacity}
                    onChange={(e) => setFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Number of Beds *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    max="10"
                    value={formData.beds}
                    onChange={(e) => setFormData(prev => ({ ...prev, beds: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Currency
                  </label>
                  <div className="w-full px-3 py-2 bg-[#FAF7F2] border border-[#E5E3DF] rounded-[12px] text-[#5c5c5c]">
                    INR (₹)
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Description
                  </label>
                  <textarea
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="Describe the room, its features, and what makes it special for pets and their owners..."
                  />
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Pricing</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    MRP *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.price.mrp}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      price: { ...prev.price, mrp: parseFloat(e.target.value) }
                    }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Discounted Price *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.price.discountedPrice}
                    onChange={(e) => {
                      const discountedPrice = parseFloat(e.target.value) || 0;
                      setFormData((prev) => ({
                        ...prev,
                        price: {
                          ...prev.price,
                          discountedPrice,
                          taxes: roomTaxesFromDiscounted(discountedPrice),
                        },
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Taxes (₹)
                    <span className="font-normal text-gray-500 ml-1">(included in discounted — auto)</span>
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={roomTaxesFromDiscounted(formData.price.discountedPrice).toFixed(2)}
                    className="w-full px-3 py-2 border border-gray-200 bg-gray-50 text-gray-700 rounded-lg cursor-not-allowed"
                    tabIndex={-1}
                  />
                </div>
              </div>
              <RoomWeekdayPricingFields
                price={formData.price}
                setPrice={(next) => setFormData((prev) => ({ ...prev, price: next }))}
              />
              <RoomGuestPricingFields
                price={formData.price}
                setPrice={(next) => setFormData((prev) => ({ ...prev, price: next }))}
                capacity={formData.capacity}
              />
            </div>

            {/* Room Options */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Room Options</h2>
              <div className="space-y-4">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.options.roomOnly}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      options: { ...prev.options, roomOnly: e.target.checked }
                    }))}
                    className="w-4 h-4 text-[#FF5E4C] border-[#E5E3DF] rounded focus:ring-[#FF5E4C]/30"
                  />
                  <span className="text-[#5c5c5c]">Room Only</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.options.withBreakfast}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      options: { ...prev.options, withBreakfast: e.target.checked }
                    }))}
                    className="w-4 h-4 text-[#FF5E4C] border-[#E5E3DF] rounded focus:ring-[#FF5E4C]/30"
                  />
                  <span className="text-[#5c5c5c]">With Breakfast</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.options.withSpa}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      options: { ...prev.options, withSpa: e.target.checked }
                    }))}
                    className="w-4 h-4 text-[#FF5E4C] border-[#E5E3DF] rounded focus:ring-[#FF5E4C]/30"
                  />
                  <span className="text-[#5c5c5c]">With Spa Services</span>
                </label>
              </div>
            </div>

            {/* Images */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Room Images</h2>
              
              <div className="border-2 border-dashed border-[#E5E3DF] rounded-[12px] p-6 mb-6 bg-[#FAF7F2]/40">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Upload room images</p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="room-image-upload"
                  />
                  <label
                    htmlFor="room-image-upload"
                    className="inline-flex items-center px-4 py-2 bg-[#FF5E4C] text-white rounded-full hover:opacity-90 cursor-pointer transition-colors"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Choose Images
                  </label>
                </div>
              </div>

              {formData.images.length > 0 && (
                <DraggableStayImagesGrid
                  images={formData.images}
                  onReorder={reorderRoomImages}
                  onRemove={removeImage}
                  hintText="Drag to reorder. The first image is used as the room cover in galleries and booking."
                  altPrefix="Room"
                />
              )}
            </div>

            {/* Features */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Room Features</h2>
              
              {/* Add Feature */}
              <div className="space-y-4 mb-4">
                {/* Feature Title */}
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">Feature Title</label>
                  <input
                    type="text"
                    value={newFeature.title}
                    onChange={(e) => setNewFeature(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="Enter feature title"
                  />
                </div>

                {/* Image Input Options */}
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">Feature Image</label>
                  
                  {/* Toggle between URL and Upload */}
                  <div className="flex space-x-2 mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        setUseImageUpload(false);
                        setNewFeature(prev => ({ ...prev, image: '' }));
                        setFeatureImageFile(null);
                        setFeatureImagePreview(null);
                      }}
                      className={`px-3 py-1 text-sm rounded-md ${
                        !useImageUpload ? 'bg-[#FF5E4C]/15 text-[#FF5E4C]' : 'bg-[#FAF7F2] text-[#78736A] border border-[#E5E3DF]'
                      }`}
                    >
                      Enter URL
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUseImageUpload(true);
                        setNewFeature(prev => ({ ...prev, image: '' }));
                        setFeatureImageFile(null);
                        setFeatureImagePreview(null);
                      }}
                      className={`px-3 py-1 text-sm rounded-md ${
                        useImageUpload ? 'bg-[#FF5E4C]/15 text-[#FF5E4C]' : 'bg-[#FAF7F2] text-[#78736A] border border-[#E5E3DF]'
                      }`}
                    >
                      Upload Image
                    </button>
                  </div>

                  {/* URL Input */}
                  {!useImageUpload && (
                    <input
                      type="url"
                      value={newFeature.image}
                      onChange={(e) => setNewFeature(prev => ({ ...prev, image: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                      placeholder="https://example.com/image.jpg"
                    />
                  )}

                  {/* File Upload */}
                  {useImageUpload && (
                    <div className="space-y-3">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFeatureImageChange}
                        className="hidden"
                        id="room-feature-image-upload"
                        disabled={uploadingFeatureImage}
                      />
                      <label
                        htmlFor="room-feature-image-upload"
                        className="flex items-center justify-center w-full px-4 py-2 border-2 border-dashed border-[#E5E3DF] rounded-[12px] cursor-pointer hover:border-[#FF5E4C]/50 hover:bg-[#FAF7F2] transition-colors"
                      >
                        <div className="text-center">
                          <ImageIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">
                            {uploadingFeatureImage ? 'Uploading...' : 'Click to upload image'}
                          </p>
                          <p className="text-xs text-gray-500">Max 10MB, JPEG, PNG, WebP, GIF</p>
                        </div>
                      </label>

                      {/* Upload Progress */}
                      {uploadingFeatureImage && (
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#FF5E4C] border-t-transparent"></div>
                          <span className="text-sm text-[#FF5E4C]">Uploading image...</span>
                        </div>
                      )}

                      {/* Image Preview */}
                      {featureImagePreview && (
                        <div className="relative">
                          <img
                            src={featureImagePreview}
                            alt="Feature preview"
                            className="w-20 h-20 object-cover rounded-lg border"
                          />
                        </div>
                      )}

                      {/* Uploaded Image Display */}
                      {newFeature.image && !uploadingFeatureImage && (
                        <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                          <img
                            src={newFeature.image}
                            alt="Uploaded feature"
                            className="w-12 h-12 object-cover rounded"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-green-800">Image uploaded successfully</p>
                            <p className="text-xs text-green-600 truncate">{newFeature.image}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setNewFeature(prev => ({ ...prev, image: '' }));
                              setFeatureImageFile(null);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Add Button */}
                <button
                  type="button"
                  onClick={addFeature}
                  disabled={!newFeature.title || !newFeature.image || uploadingFeatureImage}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingFeatureImage ? 'Uploading...' : 'Add Feature'}
                </button>
              </div>

              <div className="space-y-3">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-[12px] border border-[#E5E3DF]/60">
                    <div className="flex items-center space-x-3">
                      {feature.image ? (
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="w-10 h-10 object-cover rounded-lg border"
                        />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-gray-400" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{feature.title}</p>
                        <p className="text-sm text-gray-500 truncate max-w-xs">{feature.image}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Amenities</h2>
              
              <div className="flex gap-4 mb-4">
                <input
                  type="text"
                  value={newAmenity}
                  onChange={(e) => setNewAmenity(e.target.value)}
                  className="flex-1 px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  placeholder="Add amenity"
                />
                <button
                  type="button"
                  onClick={addAmenity}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-[#FAF7F2] text-black border border-[#E5E3DF] px-3 py-1 rounded-full">
                    <span className="text-sm">{amenity}</span>
                    <button
                      type="button"
                      onClick={() => removeAmenity(index)}
                      className="text-[#FF5E4C] hover:opacity-80"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={closeRoomForm}
                className="px-6 py-3 text-[#5c5c5c] border border-[#E5E3DF] bg-white hover:bg-[#FAF7F2] rounded-full transition-colors text-[14px] font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-[#FF5E4C] text-white rounded-full hover:opacity-90 disabled:opacity-50 transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                <span>{loading ? 'Saving...' : 'Save Room'}</span>
              </button>
            </div>
          </form>
      </div>
    );
  }

  const roomStatItems = [
    { label: 'Total rooms', value: rooms.length, icon: Bed },
    {
      label: 'Total capacity',
      value: rooms.reduce((sum, room) => sum + (room.capacity || 0), 0),
      icon: Users
    },
    {
      label: 'Avg price',
      value:
        rooms.length > 0
          ? `₹${Math.round(
              rooms.reduce((sum, room) => sum + minDiscountedAcrossWeek(room), 0) / rooms.length
            )}`
          : '₹0',
      icon: DollarSign
    },
    { label: 'Stays', value: stays.length, icon: Building2 }
  ];

  return (
    <div>
      <AdminPageHeader title="Rooms" subtitle="Configure room types and pricing">
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="px-5 py-2.5 bg-[#FF5E4C] text-white rounded-full hover:opacity-90 transition-opacity inline-flex items-center gap-2 text-[14px] font-medium"
        >
          <Plus className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
          <span>Add room</span>
        </button>
      </AdminPageHeader>

      <AdminSearchToolbar
        placeholder="Search by room name, stay, room ID, or stay ID…"
        value={listSearch}
        onChange={(e) => setListSearch(e.target.value)}
      />

      <AdminStatGrid items={roomStatItems} loading={false} />

        {/* Rooms List */}
        <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF]">
          <div className="p-5 md:p-6 border-b border-[#E5E3DF]">
            <AdminSectionTitle>All rooms</AdminSectionTitle>
          </div>
          {pagedRooms.length === 0 ? (
            <div className="p-10 text-center text-[#78736A] text-[14px]">No rooms match your search.</div>
          ) : (
            <div className="divide-y divide-[#E5E3DF]">
              {pagedRooms.map((room) => (
                <div key={`${room.stayId}-${room.roomId}`} className="p-5 md:p-6 hover:bg-[#FAF7F2]/50 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center space-x-4 min-w-0">
                      <div className="w-16 h-16 rounded-[12px] bg-[#FAF7F2] border border-[#E5E3DF] flex items-center justify-center shrink-0">
                        <Bed className="w-7 h-7 text-[#FF5E4C]" strokeWidth={ADMIN_ICON_STROKE} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-[16px] font-medium text-black truncate">{room.title}</h3>
                        </div>
                        <p className="text-gray-600 truncate">
                          {room.stayInfo?.title ||
                            stays.find((s) => s.stayId === room.stayId)?.title ||
                            'Unknown stay'}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                          <div className="flex items-center space-x-1">
                            <Users className="w-4 h-4 text-[#78736A]" strokeWidth={ADMIN_ICON_STROKE} />
                            <span className="text-sm text-gray-600">{room.capacity} guests</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Bed className="w-4 h-4 text-[#78736A]" strokeWidth={ADMIN_ICON_STROKE} />
                            <span className="text-sm text-gray-600">{room.beds} beds</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <DollarSign className="w-4 h-4 text-[#78736A]" strokeWidth={ADMIN_ICON_STROKE} />
                            <span className="text-sm text-gray-600">
                              ₹{minDiscountedAcrossWeek(room) || '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <AdminTableIconButton onClick={() => handleEdit(room)} title="Edit">
                        <Edit className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                      </AdminTableIconButton>
                      <AdminTableIconButton title="View">
                        <Eye className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                      </AdminTableIconButton>
                      <AdminTableIconButtonDanger
                        onClick={() => handleDelete(room.stayId, room.roomId)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                      </AdminTableIconButtonDanger>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <AdminPaginationBar
            page={safeListPage}
            pageSize={DEFAULT_ADMIN_PAGE_SIZE}
            total={filteredTotal}
            onPageChange={setListPage}
          />
        </div>
    </div>
  );
};

export default AdminRooms;
